import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  breakpoints,
  useWindowDimensions,
} from "../../core/hooks/dimensionProvider";
import { useOutsideClick } from "../../core/hooks/useOutsideClick";
import { usePageTitle } from "../../core/hooks/usePageTitle";
import useLanguage from "../../core/i18n/useLanguage";
import useTranslations from "../../core/i18n/useTranslations";
import { SubNav } from "../../core/interfaces/SubNav";
import usePageState from "../../core/pagestate/usePageState";
import useUser from "../../core/user/useUser";
import Divider from "../../ui-lib/components/Divider/Divider";
import Icon from "../../ui-lib/components/Icon";
import SideBar from "../../ui-lib/components/Menus/SideBar/SideBar";
import SideBarSecondary from "../../ui-lib/components/Menus/SideBar/SideBarSecondary";
import { Column } from "../Layout/Layout";
import { SubNavContext } from "../LoggedinPage/SubNavContext";
import styles from "./LoggedinPage.module.css";
import useMenu from "./useMenu";
import { ImpersonationHeader } from "../Administrator/ImpersonationHeader/ImpersonationHeader";
import LoadingSpinner from "../../ui-lib/components/Loading/LoadingSpinner";

const LoggedInPage = ({ children }: { children: ReactElement }) => {
  const user = useUser();
  const menu = useMenu(user);
  const t = useTranslations();
  const pageState = usePageState();
  const location = useLocation();
  const lang = useLanguage();

  // Navigation setup
  const menuRef = useRef(null);
  const [subPageNav, setSubPageNav] = useState<SubNav>();
  const mainNavigation = Object.values(menu).filter((m) => m.menu === "main");
  const footerNavigation = Object.values(menu).filter(
    (m) => m.menu === "footer",
  );
  const isLoading = lang.isLanguagesLoading;

  // Update page state on location change
  useEffect(() => {
    if (Object.keys(menu).length > 0) {
      const menuItem = menu[location.pathname];
      if (menuItem) {
        pageState.setPageSubTitle(t(menuItem.subtitle ?? t("Menu:empty")));
        pageState.setPageIcon(menuItem.icon);
        pageState.setPath(menuItem.to ?? "");
      }
    }
  }, [location.pathname, lang.currentLanguage]);

  usePageTitle();

  // Adjust navigation based on screen size
  const { width } = useWindowDimensions();
  const closeNav = () => pageState.setShowNavigation(false);
  useOutsideClick(menuRef, () => {
    if (width < breakpoints.desktop && pageState.showNavigation) {
      pageState.setShowNavigation(false);
    }
  });

  const contextData = useMemo(
    () => ({ setSubNav: setSubPageNav }),
    [setSubPageNav],
  );

  return (
    <main
      id={styles.siteContent}
      className={classNames({
        [styles.hasSecondary]: subPageNav && width < breakpoints.desktop,
        [styles.showNavigation]: pageState.showNavigation,
      })}
    >
      <div className={styles.overlay} />
      <div
        ref={menuRef}
        id={styles.sidebarMenu}
        className={classNames({
          hasSecondary: subPageNav && width < breakpoints.desktop,
        })}
      >
        {isLoading ? (
          <LoadingSpinner theme="primary" />
        ) : (
          <>
            <SideBar
              mainNavigation={mainNavigation}
              footerNavigation={footerNavigation}
              closeNavigation={closeNav}
              expandText={t("Menu:expand")}
              minimizeText={t("Menu:minimize")}
              minimized={subPageNav && width < breakpoints.desktop}
            />
            {subPageNav && (
              <>
                <Divider vertical />
                <SideBarSecondary
                  back={{
                    to: subPageNav.backUrl,
                    text: subPageNav.backText,
                  }}
                  closeAction={closeNav}
                  items={subPageNav.menu.map((item, i) => ({
                    id: i,
                    text: t(`${item.key}`),
                    to: item.to,
                    icon: item.icon ?? "",
                    subItems: item.subItems,
                  }))}
                />
              </>
            )}
            {width < breakpoints.desktop && (
              <button
                onClick={closeNav}
                type="button"
                className={styles.closeMenuButton}
                aria-label="Close"
              >
                <Icon name="x" color="Grey-400" />
              </button>
            )}
          </>
        )}
      </div>
      <Divider vertical />
      <Column type="top" className={styles.main}>
        {user.impersonatedFrom && <ImpersonationHeader />}
        <Column align="start" type="top" className={styles.content}>
          <SubNavContext.Provider value={contextData}>
            {isLoading ? <LoadingSpinner theme="primary" /> : children}
          </SubNavContext.Provider>
        </Column>
      </Column>
    </main>
  );
};

export default observer(LoggedInPage);
